import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { BiFilter, BiEdit } from "react-icons/bi";
import { MdDelete } from "react-icons/md";
import { Select } from "antd";
import swal from "sweetalert";
import ScrollContainer from "react-indiana-drag-scroll";
import Moment from "react-moment";
import Pagination from "./Pagination";

const { Option } = Select;
const STATUS_STYLES = {
  scheduled: { background: "#f5f5f5", color: "#595959", border: "1px solid #d9d9d9" },
  live: { background: "#f6ffed", color: "#389e0d", border: "1px solid #b7eb8f" },
  ended: { background: "#fff7e6", color: "#d46b08", border: "1px solid #ffd591" },
  finalized: { background: "#f9f0ff", color: "#722ed1", border: "1px solid #d3adf7" },
};
const RACE_RESULT_STATUS_STYLES = {
  pending: { background: "#f5f5f5", color: "#595959", border: "1px solid #d9d9d9" },
  partial: { background: "#fff7e6", color: "#d46b08", border: "1px solid #ffd591" },
  announced: { background: "#e6f7ff", color: "#096dd9", border: "1px solid #91d5ff" },
  settled: { background: "#f6ffed", color: "#389e0d", border: "1px solid #b7eb8f" },
};

const getRaceResultStatusLabel = (competition) => {
  const raceLinks = competition?.CompetitionDataOfRaceV2 || [];
  if (!raceLinks.length) return "pending";

  const total = raceLinks.length;
  const settledCount = raceLinks.filter((row) => Boolean(row?.SettledAtUtc)).length;
  if (settledCount === total) return "settled";

  const announcedStatuses = new Set(["announced", "completed"]);
  const announcedCount = raceLinks.filter((row) =>
    announcedStatuses.has(String(row?.RaceDataOfCompetitionV2?.ResultStatus || "").toLowerCase().trim())
  ).length;

  if (announcedCount === 0) return "pending";
  if (announcedCount === total) return "announced";
  return "partial";
};

const CompetitionV2Listing = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const postsPerPage = 10;

  const filteredRows = useMemo(() => {
    if (!statusFilter) return rows;
    return rows.filter((item) => item.Status === statusFilter);
  }, [rows, statusFilter]);

  const fetchRows = async ({ status = statusFilter, page = currentPage, search = keyword } = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (search) params.set("keyword", search);
      params.set("page", String(page));
      params.set("size", String(postsPerPage));
      const query = `?${params.toString()}`;
      const response = await axios.get(`${window.env.API_URL}/competition-v2/admin${query}`);
      setRows(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
      setTotalCount(response?.data?.totalcount || 0);
      setCurrentPage(response?.data?.page || page);
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      swal("Error!", message || "Could not fetch Competition V2 list", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLifecycle = async () => {
    try {
      await axios.post(`${window.env.API_URL}/competition-v2/lifecycle/run`);
      swal("Success!", "Lifecycle completed successfully", "success");
      fetchRows({ status: statusFilter, page: currentPage, search: keyword });
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      swal("Error!", message || "Could not run lifecycle", "error");
    }
  };

  const refreshCompetitions = () =>
    fetchRows({ status: statusFilter, page: currentPage, search: keyword });

  const handleDeleteCompetition = (competitionId) => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this Competition V2?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;
      try {
        await axios.delete(`${window.env.API_URL}/competition-v2/${competitionId}`);
        swal("Success!", "Competition V2 deleted successfully", "success");
        fetchRows({ status: statusFilter, page: currentPage, search: keyword });
      } catch (error) {
        const message = error?.response?.data?.message || error.message;
        swal("Error!", message || "Could not delete Competition V2", "error");
      }
    });
  };

  return (
    <div className="page">
      <div className="rightsidedata">
        <div style={{ marginTop: "30px" }}>
          <div className="Header ">
            <h4>Competition Listing</h4>
            <div>
              <button onClick={runLifecycle}>
                Run Lifecycle
              </button>
              <button style={{ marginLeft: "8px" }} onClick={refreshCompetitions}>
                Refresh Competitions
              </button>
              <button style={{ marginLeft: "8px" }} onClick={() => navigate("/competition/create")}>Add Competition</button>
              
              <span className="addmore">
                <BiFilter className="calendericon" onClick={() => setShowFilter(!showFilter)} />
              </span>
            </div>
          </div>

          {showFilter ? (
            <div className="userfilter userFilter">
              <div className="filtertextform filterTextForm">
                <input
                  type="text"
                  className="form-control"
                  value={keyword}
                  placeholder="Search by name/code"
                  onChange={(e) => setKeyword(e.target.value)}
                />
                <Select
                  value={statusFilter || undefined}
                  allowClear
                  placeholder="Filter by status"
                  style={{ width: "100%" }}
                  onChange={(value) => setStatusFilter(value || "")}
                >
                  <Option value="scheduled">scheduled</Option>
                  <Option value="live">live</Option>
                  <Option value="ended">ended</Option>
                  <Option value="finalized">finalized</Option>
                </Select>
              </div>
              <button
                className="filterbtn filterBtn"
                onClick={() => {
                  setCurrentPage(1);
                  fetchRows({ status: statusFilter, page: 1, search: keyword });
                }}
              >
                Apply Filter
              </button>
            </div>
          ) : null}

          <div
            className="div_maintb"
            style={{
              height: "auto",
              maxHeight: "80vh",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: "8px",
              marginBottom: "32px",
            }}
          >
            <ScrollContainer style={{ overflowX: "auto", overflowY: "visible" }}>
              <table style={{ minWidth: "1320px" }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: "240px" }}>Action</th>
                    <th style={{ minWidth: "300px" }}>Name</th>
                    <th style={{ minWidth: "300px" }}>Code</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Races Status</th>
                    <th>Winners Published</th>
                    <th>Allowed Selections</th>
                    <th>Sponsor</th>
                    <th>Races</th>
                    <th style={{ minWidth: "220px" }}>Start</th>
                    <th style={{ minWidth: "220px" }}>End</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={12}>Loading...</td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={12}>No competition data found.</td>
                    </tr>
                  ) : (
                    filteredRows.map((item) => (
                      <tr className="tr_table_class" key={item._id}>
                        <td className="table_delete_btn1">
                          <MdDelete
                            style={{ marginRight: "8px", fontSize: "20px", cursor: "pointer" }}
                            onClick={() => handleDeleteCompetition(item._id)}
                          />
                          <BiEdit
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate("/competition/manage", {
                                state: { competitionId: item._id },
                              })
                            }
                          />
                          <button
                            className="Approvedbtn purple"
                            style={{ marginLeft: "8px", padding: "2px 8px", minWidth: "58px" }}
                            onClick={() =>
                              navigate("/competition/races", {
                                state: { competitionId: item._id },
                              })
                            }
                          >
                            Races
                          </button>
                        </td>
                        <td>{item.NameEn || "N/A"}</td>
                        <td>{item.CompetitionCode || "N/A"}</td>
                        <td>{item.CompetitionCategory || "N/A"}</td>
                        <td>
                          {(() => {
                            const statusStyle =
                              STATUS_STYLES[item.Status] ||
                              { background: "#fafafa", color: "#262626", border: "1px solid #d9d9d9" };
                            return (
                          <span
                            className="Approvedbtn"
                            style={{
                              padding: "4px 10px",
                              ...statusStyle,
                            }}
                          >
                            {item.Status}
                          </span>
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const raceResultStatus = getRaceResultStatusLabel(item);
                            const raceStatusStyle =
                              RACE_RESULT_STATUS_STYLES[raceResultStatus] ||
                              { background: "#fafafa", color: "#262626", border: "1px solid #d9d9d9" };
                            return (
                              <span
                                className="Approvedbtn"
                                style={{
                                  padding: "4px 10px",
                                  ...raceStatusStyle,
                                }}
                              >
                                {raceResultStatus}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {item.WinnersPublishedAtUtc ? (
                            <span
                              className="Approvedbtn"
                              style={{
                                padding: "4px 10px",
                                background: "#f6ffed",
                                color: "#389e0d",
                                border: "1px solid #b7eb8f",
                              }}
                            >
                              published
                            </span>
                          ) : Number(item.WinnerCount || 0) > 0 ? (
                            <span
                              className="Approvedbtn"
                              style={{
                                padding: "4px 10px",
                                background: "#fff7e6",
                                color: "#d46b08",
                                border: "1px solid #ffd591",
                              }}
                            >
                              draft
                            </span>
                          ) : (
                            <span
                              className="Approvedbtn"
                              style={{
                                padding: "4px 10px",
                                background: "#fafafa",
                                color: "#8c8c8c",
                                border: "1px solid #d9d9d9",
                              }}
                            >
                              not generated
                            </span>
                          )}
                        </td>
                        <td>{item.AllowedHorseSelections}</td>
                        <td>{item?.CompetitionSponsorDataV2?.TitleEn || "N/A"}</td>
                        <td>{item?.CompetitionDataOfRaceV2?.length || 0}</td>
                        <td>
                          <Moment format="YYYY/MM/DD HH:mm">{item.StartDateTimeUtc}</Moment>
                        </td>
                        <td>
                          <Moment format="YYYY/MM/DD HH:mm">{item.EndDateTimeUtc}</Moment>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ScrollContainer>
          </div>
          <Pagination
            postsPerPage={postsPerPage}
            totalPosts={totalCount}
            paginate={(pageNumber) => {
              if (typeof pageNumber === "number") {
                setCurrentPage(pageNumber);
                fetchRows({ status: statusFilter, page: pageNumber, search: keyword });
              }
            }}
            currentPage={currentPage}
            TotalPages={totalPages}
          />
        </div>
      </div>
    </div>
  );
};

export default CompetitionV2Listing;
